import { useEffect, useState } from "react";
import { db, auth } from "../services/firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

export default function Manpower() {

  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("All");

  // ===============================
  // LOAD DATA
  // ===============================
  const loadData = async () => {
    const snap = await getDocs(collection(db, "manpower"));
    const data = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
    }));
    setItems(data);
  };

  useEffect(() => {
    loadData();
  }, []);

  // ===============================
  // DYNAMIC STATE FILTER
  // ===============================
  const states = [
    "All",
    ...new Set(items.map(item => item.state).filter(Boolean)),
  ];

  // ===============================
  // SEARCH + FILTER
  // ===============================
  const filteredItems = items.filter(item => {

    const searchLower = search.toLowerCase();

    const matchSearch =
      item.name?.toLowerCase().includes(searchLower) ||
      item.agencyName?.toLowerCase().includes(searchLower) ||
      item.skill?.toLowerCase().includes(searchLower) ||
      item.skillRequired?.toLowerCase().includes(searchLower);

    const matchState =
      stateFilter === "All" || item.state === stateFilter;

    return matchSearch && matchState;
  });

  // ===============================
  // SINGLE USER SINGLE RATING
  // ===============================
  const rate = async (manpowerId, value) => {

    if (!auth.currentUser) {
      alert("Login required");
      return;
    }

    const userId = auth.currentUser.uid;

    const ratingRef = doc(
      db,
      "manpower",
      manpowerId,
      "ratings",
      userId
    );

    // Save / Update user rating
    await setDoc(ratingRef, {
      rating: value,
    });

    // Recalculate average
    const ratingsSnap = await getDocs(
      collection(db, "manpower", manpowerId, "ratings")
    );

    let total = 0;
    ratingsSnap.forEach((doc) => {
      total += doc.data().rating;
    });

    const count = ratingsSnap.size;
    const average = count > 0 ? total / count : 0;

    // Update main manpower doc
    await setDoc(
      doc(db, "manpower", manpowerId),
      {
        rating: average,
        totalRatings: count,
      },
      { merge: true }
    );

    loadData();
  };

  // ===============================
  // DELETE (OWNER ONLY)
  // ===============================
  const handleDelete = async (id, ownerUid) => {
    if (auth.currentUser?.uid !== ownerUid) {
      alert("You can delete only your post.");
      return;
    }
    await deleteDoc(doc(db, "manpower", id));
    loadData();
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">
          Manpower Marketplace 👷
        </h1>

        {auth.currentUser && (
          <button
            onClick={() => navigate("/admin/add-manpower")}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            + Add Manpower
          </button>
        )}
      </div>

      {/* SEARCH + STATE FILTER */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <input
          placeholder="Search by name or skill..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border p-2 rounded w-60"
        />

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="border p-2 rounded"
        >
          {states.map((state, index) => (
            <option key={index} value={state}>
              {state}
            </option>
          ))}
        </select>
      </div>

      {/* CARDS */}
      {filteredItems.length === 0 ? (
        <p>No manpower found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden transition transform hover:scale-[1.02] hover:shadow-2xl"
            >

              {/* IMAGE */}
             <div className="w-full h-60 bg-gray-100 flex items-center justify-center overflow-hidden">
            <img
                src={item.images?.[0]}
                alt="profile"
                className="max-h-full max-w-full object-contain"
            />
            </div>


              <div className="p-5">

                {/* NAME + TYPE BADGE */}
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">
                    {item.type === "Agency"
                      ? item.agencyName
                      : item.name}
                  </h2>

                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                    item.type === "Agency"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-blue-100 text-blue-700"
                  }`}>
                    {item.type}
                  </span>
                </div>

                <p className="text-sm text-gray-500 mt-1">
                  📍 {item.state}
                </p>

                {/* ===================== */}
                {/* INDIVIDUAL DISPLAY */}
                {/* ===================== */}
                {item.type === "Individual" && (
                  <div className="mt-3 text-sm text-gray-700 space-y-1">
                    <p><b>Skill:</b> {item.skill}</p>
                    <p><b>Experience:</b> {item.experience} years</p>
                    <p><b>Qualification:</b> {item.qualification}</p>
                    <p><b>Expected Wage:</b> ₹{item.expectedWage}</p>
                    <p>
  <b>Languages:</b>{" "}
  {Array.isArray(item.languages)
    ? item.languages.join(", ")
    : item.languages || "Not specified"}
</p>

                  </div>
                )}

                {/* ===================== */}
                {/* AGENCY DISPLAY */}
                {/* ===================== */}
                {item.type === "Agency" && (
                  <div className="mt-3 text-sm text-gray-700 space-y-1">
                    <p><b>Nature of Work:</b> {item.natureOfWork}</p>
                    <p><b>Daily Wage:</b> ₹{item.dailyWage}</p>
                    <p><b>Contract:</b> {item.contractPeriod}</p>
                    <p><b>Work Location:</b> {item.workLocation}</p>
                    <p><b>Skill Required:</b> {item.skillRequired}</p>
                    {item.minimumQualification && (
                      <p><b>Min Qualification:</b> {item.minimumQualification}</p>
                    )}
                  </div>
                )}

                {/* RATING DISPLAY */}
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-yellow-500 text-lg">⭐</span>
                  <span className="font-semibold">
                    {item.rating?.toFixed(1) || 0}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({item.totalRatings || 0})
                  </span>
                </div>

                {/* RATE BUTTONS */}
                <div className="flex gap-2 mt-3 flex-wrap">
                  {[1,2,3,4,5].map((val) => (
                    <button
                      key={val}
                      onClick={() => rate(item.id, val)}
                      className="px-3 py-1 text-xs bg-yellow-400 rounded-lg hover:bg-yellow-500 transition"
                    >
                      {val}⭐
                    </button>
                  ))}
                </div>

                {/* CONTACT */}
                <a
                  href={`tel:${item.contactNumber}`}
                  className="block mt-4 bg-green-600 hover:bg-green-700 text-white text-center py-2 rounded-lg transition"
                >
                  📞 Contact
                </a>

                {/* DELETE */}
                {auth.currentUser?.uid === item.createdBy && (
                  <button
                    onClick={() => handleDelete(item.id, item.createdBy)}
                    className="text-red-600 mt-3 font-semibold"
                  >
                    Delete
                  </button>
                )}

              </div>
            </div>
          ))}

        </div>
      )}

    </div>
  );
}











